/**
 * deriveInitialSignatureName — the single, testable rule for what the patient's
 * typed signature field starts with.
 *
 * The signature is type-only (drawing was removed 2026-08-26 — drawn marks
 * rasterized poorly on the faxed PDF), so the patient always lands directly on
 * the typed field. It should already carry their name:
 *   1. the caller-supplied `defaultSignatureName` (the parent computes it from
 *      the wizard profile / signatureName), when non-empty; else
 *   2. a fallback built from the preloaded profile's first + last name, so the
 *      field is still pre-filled when the caller passed nothing.
 * Empty string when neither is available. The field stays editable either way.
 */

interface NameParts {
  first_name?: string | null;
  last_name?: string | null;
}

export function deriveInitialSignatureName(
  defaultSignatureName: string | null | undefined,
  initialProfile: NameParts | null | undefined
): string {
  const fromDefault = String(defaultSignatureName || "").trim();
  if (fromDefault) return fromDefault;
  return [initialProfile?.first_name, initialProfile?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
}

interface PlanMemberNameParts {
  plan_member_first_name?: string | null;
  plan_member_last_name?: string | null;
}

/**
 * The plan holder's leg: their typed signature starts with THEIR name, or
 * nothing. The plan-member preload blob carries the patient's `first_name`
 * for the role banner ("signing for {first_name}'s request"), and the editor
 * profile is built over it, so falling back to `first_name` proposed the
 * child's first name as a parent's signature (case imp_797e5495, principle
 * 16: a wrong default is worse than an empty box). Only the plan member's
 * own `plan_member_*` keys may seed it; with neither, it stays empty and the
 * plan holder types their name.
 */
export function deriveInitialPlanMemberSignatureName(
  defaultSignatureName: string | null | undefined,
  initialProfile: PlanMemberNameParts | null | undefined
): string {
  const fromDefault = String(defaultSignatureName || "").trim();
  if (fromDefault) return fromDefault;
  return [initialProfile?.plan_member_first_name, initialProfile?.plan_member_last_name]
    .map((s) => String(s || "").trim())
    .filter(Boolean)
    .join(" ");
}

export default deriveInitialSignatureName;
